/**
 * Folders in the presets browser: naming one (a new folder, or renaming), and the right-click
 * entries of a folder or a source. Plain functions and one signal; the edits go to the
 * presets service.
 */
import { createSignal, type Accessor } from "solid-js";
import { SEPARATOR, type MenuEntry } from "../../state/menu";
import type { Presets, SourceId } from "../../state/presets";
import { folderName, joinFolder, parentFolder } from "../../shared/presetTree";
import { toast } from "../../shared/toast";

/** A folder name being typed: a new folder inside `parent`, or `renaming` an existing one. */
export interface Naming {
  /** The source it is in. */
  source: SourceId;
  /** The folder it is (or will be) inside (`""`: the top). */
  parent: string;
  /** The folder being renamed (`null`: a new folder). */
  renaming: string | null;
}

/** Naming folders, and what the browser's tree and tiles can do with them. */
export interface Folders {
  /** The name being typed, or `null`. */
  naming: Accessor<Naming | null>;
  /** Starts typing a new folder's name inside `parent`. */
  startNew(source: SourceId, parent: string): void;
  /** Starts renaming `path`. */
  startRename(source: SourceId, path: string): void;
  /** Ends typing: makes or renames the folder with `name` (empty: nothing). */
  finish(name: string): Promise<void>;
  /** Stops typing without a change. */
  cancel(): void;
  /** Deletes a folder: what was in it goes up to its parent. */
  remove(source: SourceId, path: string): Promise<void>;
  /** The right-click entries of a folder (`path`), or of a source's top (`""`). */
  menu(source: SourceId, path: string): MenuEntry[];
}

/** Starts the folder tools of one browser. */
export function createFolders(presets: Presets): Folders {
  const [naming, setNaming] = createSignal<Naming | null>(null);

  /** Refuses (with a toast) a source that cannot be changed. */
  const writable = (source: SourceId) => {
    const found = presets.source(source);
    if (found?.readOnly) toast(found.why, "error");
    return !!found && !found.readOnly;
  };

  const folders: Folders = {
    naming,
    startNew(source, parent) {
      if (writable(source)) setNaming({ source, parent, renaming: null });
    },
    startRename(source, path) {
      if (writable(source)) setNaming({ source, parent: parentFolder(path), renaming: path });
    },
    async finish(name) {
      const now = naming();
      setNaming(null);
      if (!now || !name.trim()) return;
      const path = joinFolder(now.parent, name);
      if (now.renaming === null) {
        if (await presets.edit(now.source, { edit: "add_folder", path })) presets.go(now.source, path);
      } else if (path !== now.renaming && (await presets.edit(now.source, { edit: "rename_folder", from: now.renaming, to: path }))) {
        const { source, folder } = presets.place();
        // Browsing the renamed folder (or inside it): follow it.
        if (source === now.source && (folder === now.renaming || folder.startsWith(`${now.renaming}/`))) presets.go(source, path + folder.slice(now.renaming.length));
      }
    },
    cancel: () => setNaming(null),
    async remove(source, path) {
      if (!(await presets.edit(source, { edit: "remove_folder", path }))) return;
      const place = presets.place();
      if (place.source === source && (place.folder === path || place.folder.startsWith(`${path}/`))) presets.go(source, parentFolder(path));
      toast(`Folder ${folderName(path)} deleted; what was in it moved up.`);
    },
    menu(source, path) {
      const found = presets.source(source);
      const disabled = !found || found.readOnly;
      const entries: MenuEntry[] = [{ heading: path ? folderName(path) : (found?.label ?? "") }, { label: "New folder", disabled, action: () => folders.startNew(source, path) }];
      if (path) {
        entries.push({ label: "Rename folder", disabled, action: () => folders.startRename(source, path) });
        entries.push(SEPARATOR, { label: "Delete folder", danger: true, disabled, action: () => void folders.remove(source, path) });
      }
      return entries;
    },
  };
  return folders;
}
